import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import InventoryRoundedIcon from '@mui/icons-material/InventoryRounded';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';
import {
  Button,
  Chip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';

import { EmptyState, ErrorState, LoadingState, TablePageShell } from '@/shared/ui';

import { useProductStock } from '../../hooks';
import { formatDateTime, formatQuantity } from '../../lib/format';
import { useProductLocationLookup } from '../../lib/useProductLocationLookup';

export interface ProductStockPanelProps {
  productId: string;
}

/**
 * Product-360 stock block for {@code ProductEditPage} — this product's live stock snapshot across
 * every fulfilment location (`web-inventory-service`, via the same BFF endpoint the dedicated
 * `/inventory` Stock tab uses: `GET /api/v1/inventory/stock/{productId}`).
 *
 * A trimmed, product-scoped variant of `features/inventory`'s `StockPanel` — mirrors its table
 * shape (Location · On hand · Reserved · Available · UoM · Backorder) but drops the Product column
 * (redundant — the whole page is already this product) and the row-action menu (Ledger/Policy
 * management stays the dedicated Inventory page's job; this panel is read-only, a summary, not a
 * management surface). No filters/pagination either: `GET .../stock/{productId}` returns every
 * location for one product in a single, non-paginated call — a product's location count is small
 * by construction, unlike the tenant-wide Stock tab this mirrors.
 */
export function ProductStockPanel({ productId }: ProductStockPanelProps) {
  const { t } = useTranslation('catalog');
  const navigate = useNavigate();
  const { getLocationName } = useProductLocationLookup();

  const query = useProductStock(productId);
  const items = query.data?.items ?? [];
  const lastUpdatedAt = items.reduce<string | undefined>(
    (latest, row) => (!latest || row.updatedAt > latest ? row.updatedAt : latest),
    undefined,
  );

  return (
    <Stack spacing={1.5}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1}
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        justifyContent="space-between"
      >
        <Typography variant="body2" color="text.secondary">
          {lastUpdatedAt
            ? t('productEdit.productStock.lastUpdated', { datetime: formatDateTime(lastUpdatedAt) })
            : t('productEdit.productStock.subtitle')}
        </Typography>
        <Button
          size="small"
          startIcon={<OpenInNewRoundedIcon fontSize="small" />}
          onClick={() => navigate('/inventory')}
        >
          {t('productEdit.productStock.openInInventory')}
        </Button>
      </Stack>

      {query.isLoading ? (
        <LoadingState label={t('productEdit.productStock.loading')} />
      ) : query.isError ? (
        <ErrorState
          title={t('productEdit.productStock.loadError.title')}
          message={query.error?.message ?? t('common:error.unknown')}
          onRetry={() => void query.refetch()}
        />
      ) : items.length === 0 ? (
        <EmptyState
          title={t('productEdit.productStock.empty.title')}
          description={t('productEdit.productStock.empty.description')}
          icon={<InventoryRoundedIcon />}
        />
      ) : (
        <TablePageShell>
          <TableContainer sx={{ maxHeight: 360 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>{t('productEdit.productStock.table.location')}</TableCell>
                  <TableCell align="right">{t('productEdit.productStock.table.onHand')}</TableCell>
                  <TableCell align="right">{t('productEdit.productStock.table.reserved')}</TableCell>
                  <TableCell align="right">{t('productEdit.productStock.table.available')}</TableCell>
                  <TableCell>{t('productEdit.productStock.table.uom')}</TableCell>
                  <TableCell>{t('productEdit.productStock.table.backorder')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((row) => (
                  <TableRow key={row.id} hover>
                    <TableCell>
                      <Tooltip title={row.locationId}>
                        <Typography variant="body2">{getLocationName(row.locationId)}</Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell align="right">{formatQuantity(row.onHandQty)}</TableCell>
                    <TableCell align="right">{formatQuantity(row.reservedQty)}</TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" fontWeight={600}>
                        {formatQuantity(row.availableQty)}
                      </Typography>
                    </TableCell>
                    <TableCell>{row.uom}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        variant="outlined"
                        color={row.backorderAllowed ? 'success' : 'default'}
                        label={
                          row.backorderAllowed
                            ? t('productEdit.productStock.backorderChip.allowed')
                            : t('productEdit.productStock.backorderChip.notAllowed')
                        }
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </TablePageShell>
      )}
    </Stack>
  );
}
